// Types, libellés et petits composants partagés du module « entrée ».
import { useState, type ReactNode } from "react";
import { StatusChip, useToast, type CheckStatus } from "@core/ui";

export type Doc = {
  id: string;
  title: string;
  filename: string;
  mime: string;
  size_bytes: number;
  sha256: string;
  version: number;
  category: string;
  created_at: string;
  deleted: boolean;
  url: string;
} | null;

export type Party = {
  id: string;
  role: string;
  role_label: string;
  full_name: string;
  email: string | null;
  phone: string | null;
  account_linked: boolean;
  from_application: boolean;
  invitation: { sent_at: string; accepted_at: string | null } | null;
};

export type Annex = {
  code: string;
  label: string;
  help: string;
  required: boolean;
  due: "signature" | "remise_des_cles";
  status: "a_fournir" | "fourni" | "non_applicable";
  note: string;
  evidence: string | null;
  document: Doc;
};

export type Clause = { code: string; title: string; text: string };

export type Prorata = {
  period_start: string;
  period_end: string;
  days_in_month: number;
  days_occupied: number;
  full_month: boolean;
  rent_cents: number;
  charges_cents: number;
  total_cents: number;
  explanation: string;
};

export type LeaseDetail = {
  lease: {
    id: string;
    property_id: string;
    campaign_id: string | null;
    application_id: string | null;
    regime: string;
    status: string;
    start_on: string;
    end_on: string | null;
    duration_months: number | null;
    rent_cents: number;
    charges_cents: number;
    charges_mode: string;
    deposit_cents: number;
    payment_day: number;
    activated_at: string | null;
  };
  regime_label: string;
  status_label: string;
  reference: string;
  furnished: boolean;
  property: { id: string; label: string; address: string; furnished: boolean; in_copropriete: boolean; dpe_class: string | null };
  terms: {
    clauses: Clause[];
    special_conditions: string;
    prefill_sources: Record<string, string>;
    approved_version_id: string | null;
    approved_at: string | null;
    approved_by: string | null;
    approval_valid: boolean;
  };
  parties: Party[];
  annexes: Annex[];
  versions: { id: string; version: number; created_at: string; matches_current_terms: boolean; approved: boolean; document: Doc }[];
  checks: { deposit_cap_cents: number | null; deposit_explanation: string; problems: string[]; warnings: string[]; ready_for_approval: boolean };
  prorata: Prorata;
  legal_notice: string;
};

export type Signer = {
  id: string;
  position: number;
  role: string;
  role_label: string;
  full_name: string;
  email: string | null;
  status: "en_attente" | "signe" | "refuse";
  signed_at: string | null;
  refused_at: string | null;
  refusal_reason: string | null;
  reminder_count: number;
  last_reminder_at: string | null;
  is_turn: boolean;
  signing_url: string | null;
  mine: boolean;
};

export type SignatureRequest = {
  id: string;
  lease_id: string;
  subject: "bail" | "etat_des_lieux";
  inspection_id: string | null;
  version: number | null;
  provider: string;
  mode: string;
  status: "en_cours" | "a_recuperer" | "signee" | "refusee" | "expiree" | "annulee";
  status_label: string;
  expires_at: string;
  refusal_reason: string | null;
  cancel_reason: string | null;
  retrieval_error: string | null;
  created_at: string;
  completed_at: string | null;
  signers: Signer[];
  signed_documents: Doc[];
  proof: Doc;
};

export type Condition = "neuf" | "bon" | "usage" | "mauvais" | "hors_service";
export type Item = { key: string; name: string; condition: Condition | null; comment: string; source_key?: string | null };
export type Room = { key: string; name: string; comment: string; items: Item[]; source_key?: string | null };
export type Meter = { key: string; kind: string; label: string; number: string; reading: string; unit: string };
export type KeySet = { key: string; label: string; count: number };
export type InspectionContent = {
  rooms: Room[];
  meters: Meter[];
  keys: KeySet[];
  general_comment: string;
  participants: { name: string; quality: string }[];
};
export type Photo = { id: string; room_key: string; item_key: string | null; document_id: string; caption: string; client_key: string | null; created_at: string; url: string };

export type Inspection = {
  id: string;
  lease_id: string;
  kind: "entree" | "sortie";
  kind_label: string;
  status: "brouillon" | "en_signature" | "signe";
  status_label: string;
  version: number;
  supersedes_id: string | null;
  superseded_by: string | null;
  revision: number;
  scheduled_at: string | null;
  performed_at: string | null;
  content: InspectionContent;
  content_sha256: string | null;
  landlord_signed_at: string | null;
  landlord_signer_name: string | null;
  tenant_signed_at: string | null;
  tenant_signer_name: string | null;
  tenant_signature_mode: string | null;
  tenant_absence_note: string | null;
  reserves: string;
  pdf: Doc;
  photos: Photo[];
  stats: { rooms: number; items: number; items_without_condition: number; items_without_photo: number; photos: number };
  signature_request_id: string | null;
  updated_at: string;
  lease?: { id: string; status: string; regime: string; property_label: string; property_address: string; furnished: boolean; parties: { id: string; role: string; full_name: string; account_linked: boolean }[] };
};

export const CONDITIONS: { value: Condition; label: string; short: string }[] = [
  { value: "neuf", label: "Neuf", short: "Neuf" },
  { value: "bon", label: "Bon état", short: "Bon" },
  { value: "usage", label: "État d’usage", short: "Usage" },
  { value: "mauvais", label: "Mauvais état", short: "Mauvais" },
  { value: "hors_service", label: "Hors service", short: "H.S." },
];

export function conditionLabel(c: Condition | null | undefined): string {
  return CONDITIONS.find((x) => x.value === c)?.label ?? "Non renseigné";
}

export const METER_KINDS = [
  { value: "electricite", label: "Électricité" },
  { value: "gaz", label: "Gaz" },
  { value: "eau_froide", label: "Eau froide" },
  { value: "eau_chaude", label: "Eau chaude" },
  { value: "chauffage", label: "Chauffage" },
  { value: "autre", label: "Autre compteur" },
];

export const REGIMES = [
  { value: "vide", label: "Logement vide" },
  { value: "meuble", label: "Logement meublé" },
  { value: "colocation", label: "Colocation" },
  { value: "mobilite", label: "Bail mobilité" },
  { value: "etudiant", label: "Bail étudiant (meublé)" },
];

/** Statut du bail : icône + mot (jamais la couleur seule). */
export function LeaseStatus({ status, label }: { status: string; label?: string }) {
  const map: Record<string, CheckStatus> = {
    brouillon: "info",
    en_signature: "a_confirmer",
    signe: "conforme",
    actif: "conforme",
    preavis: "a_confirmer",
    termine: "non_concluant",
    resilie: "non_concluant",
  };
  const labels: Record<string, string> = {
    brouillon: "Brouillon",
    en_signature: "En signature",
    signe: "Signé",
    actif: "Location active",
    preavis: "Préavis",
    termine: "Terminé",
    resilie: "Résilié",
  };
  return <StatusChip status={map[status] ?? "info"} label={label ?? labels[status] ?? status} />;
}

export function RequestStatus({ r }: { r: Pick<SignatureRequest, "status" | "status_label"> }) {
  const map: Record<string, CheckStatus> = {
    en_cours: "a_confirmer",
    a_recuperer: "a_confirmer",
    signee: "conforme",
    refusee: "anomalie",
    expiree: "non_concluant",
    annulee: "non_concluant",
  };
  return <StatusChip status={map[r.status] ?? "info"} label={r.status_label} />;
}

export function SignerStatus({ s }: { s: Signer }) {
  if (s.status === "signe") return <StatusChip status="conforme" label="Signé" />;
  if (s.status === "refuse") return <StatusChip status="anomalie" label="Refusé" />;
  return <StatusChip status="manquant" label={s.is_turn ? "Invité, en attente" : "En attente de son tour"} />;
}

export function InspectionStatus({ i }: { i: Pick<Inspection, "status" | "status_label"> }) {
  const map: Record<string, CheckStatus> = { brouillon: "info", en_signature: "a_confirmer", signe: "conforme" };
  return <StatusChip status={map[i.status] ?? "info"} label={i.status_label} />;
}

/** Empreinte d'un document, affichée en données (F-041). */
export function Fingerprint({ sha }: { sha: string }) {
  return (
    <span className="entree-sha" title={`SHA-256 : ${sha}`}>
      SHA-256 {sha.slice(0, 12)}…{sha.slice(-6)}
    </span>
  );
}

export function DocLink({ doc, label }: { doc: Doc; label?: ReactNode }) {
  if (!doc) return null;
  if (doc.deleted) return <span className="muted">{label ?? doc.title} (effacé)</span>;
  return (
    <span className="entree-doc">
      <a href={doc.url} target="_blank" rel="noreferrer">
        {label ?? (doc.title || doc.filename)}
      </a>{" "}
      <Fingerprint sha={doc.sha256} />
    </span>
  );
}

export const LEGAL_NOTICE = "Modèle à faire valider juridiquement (D-14) : les baux, actes et attestations produits ici sont des propositions de modèle, à relire avant tout usage réel.";

/** Identifiant local stable (pièces, éléments, photos saisies hors connexion). */
export function newKey(): string {
  try {
    return crypto.randomUUID().replace(/-/g, "");
  } catch {
    return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
  }
}

export function errMessage(e: unknown): string {
  return e instanceof Error ? e.message : "Erreur inattendue.";
}

// ---------------------------------------------------------------------------
// Exécution d'une action avec état « en cours » et message d'erreur.


export function useAction() {
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const run = async (key: string, fn: () => Promise<unknown>, ok?: string): Promise<boolean> => {
    setBusy(key);
    setError(null);
    try {
      await fn();
      if (ok) toast(ok);
      return true;
    } catch (e) {
      setError(e);
      toast(errMessage(e), "erreur");
      return false;
    } finally {
      setBusy(null);
    }
  };
  return { busy, error, run, setError };
}
